import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

// @ts-ignore
import Liquid from 'liquid-node';

import { messageTemplatesResource } from '@lib/resources';
import { selectContext } from '@lib/selectors';

const engine = new Liquid.Engine();

const MESSAGE_TEMPLATE_PREFIX = '%message_template%';

/**
 * Generate text with a message template key
 * @param id
 * @param type
 * @example
 * const text = generateMessageTemplateKey('patientLumaBotWelcome');
 * // text === '%message_template%messages.web.patientLumaBotWelcome'
 */
export const generateMessageTemplateKey = (id: string, type: 'web' | 'inapp' = 'web') =>
  `${MESSAGE_TEMPLATE_PREFIX}messages.${type}.${id}`;

export const isMessageTemplateKey = (text?: string) => text && text.indexOf(MESSAGE_TEMPLATE_PREFIX) === 0;

/**
 * Returns the message template key from a text
 * @param text
 * @example
 * const templateKey = getMessageTemplateKey('%message_template%messages.web.patientLumaBotWelcome');
 * // templateKey === 'messages.web.patientLumaBotWelcome'
 */
export const getMessageTemplateKey = (text: string) => {
  if (isMessageTemplateKey(text)) {
    return text.replace(MESSAGE_TEMPLATE_PREFIX, '');
  }
  return '';
};

export default function useMessageTemplate(value: string) {
  const [text, setText] = useState('');
  const messageTemplate = useSelector(messageTemplatesResource.makeSelectEntity(value));
  const messageTemplateValue = messageTemplate && messageTemplate.value;

  const ctxt = useSelector(selectContext);

  useEffect(() => {
    let isCancelled = false;
    async function updateText() {
      const newText = await engine.parseAndRender(messageTemplateValue, ctxt || {});
      if (!isCancelled) setText(newText);
    }
    if (messageTemplateValue) updateText();

    return () => {
      isCancelled = true;
    };
  }, [messageTemplateValue, ctxt]);

  return text;
}
